import type { LocaleCode } from "./locale-config";

export type IntakeOption = { value: string; label: string };

type IntakeCopy = {
  common: {
    photoEmpty: string;
    photoSelected: string;
    unavailable: string;
    sendError: string;
    sending: string;
  };
  emergency: {
    alertBefore: string;
    alertAfter: string;
    name: string;
    phone: string;
    email: string;
    location: string;
    city: string;
    waterEntry: string;
    roofType: string;
    photos: string;
    description: string;
    submit: string;
    yesNo: readonly IntakeOption[];
    roofTypes: readonly IntakeOption[];
    errors: {
      name: string;
      phone: string;
      email: string;
      location: string;
      city: string;
      description: string;
      option: string;
    };
  };
  planned: {
    intro: string;
    propertyLegend: string;
    roofLegend: string;
    contactLegend: string;
    propertyType: string;
    city: string;
    address: string;
    postalCode: string;
    service: string;
    approximateSize: string;
    sizePlaceholder: string;
    stories: string;
    slope: string;
    skylights: string;
    complexity: string;
    existingRoof: string;
    roofAge: string;
    agePlaceholder: string;
    desiredSystem: string;
    knownIssues: string;
    timeline: string;
    photos: string;
    name: string;
    phone: string;
    email: string;
    preferredContact: string;
    submit: string;
    propertyTypes: readonly IntakeOption[];
    timelines: readonly IntakeOption[];
    contactMethods: readonly IntakeOption[];
    roofTypes: readonly IntakeOption[];
    storiesOptions: readonly IntakeOption[];
    slopeOptions: readonly IntakeOption[];
    skylightOptions: readonly IntakeOption[];
    complexityOptions: readonly IntakeOption[];
    errors: {
      propertyType: string;
      address: string;
      city: string;
      postalCode: string;
      service: string;
      option: string;
      timeline: string;
      name: string;
      phone: string;
      email: string;
      contact: string;
    };
  };
};

const values = {
  property: ["Residential", "Commercial", "Strata", "Multi-family", "New construction"],
  timeline: ["Urgent", "Within 30 days", "1–3 months", "3–6 months", "Planning / budgeting"],
  contact: ["Phone", "Email", "Either"],
  roof: [
    "Asphalt shingles",
    "Cedar",
    "Metal",
    "Flat / low-slope membrane",
    "Tile",
    "Other",
    "Unknown",
  ],
  stories: ["1", "2", "3", "4+", "Unknown"],
  slope: ["Low", "Medium", "Steep", "Unknown"],
  skylights: ["None", "One", "Multiple", "Unknown"],
  complexity: ["Simple", "Typical", "Complex", "Unknown"],
} as const;

function options(source: readonly string[], labels: readonly string[]): IntakeOption[] {
  return source.map((value, index) => ({ value, label: labels[index] ?? value }));
}

const en: IntakeCopy = {
  common: {
    photoEmpty:
      "The current lead connection accepts JSON only. Selected photos cannot be transmitted; please send them when our team follows up.",
    photoSelected:
      "{count} photo(s) selected in your browser. No files were transmitted; please send them when our team follows up.",
    unavailable: "Online delivery is not configured. Please call us.",
    sendError: "We could not send the request. Please try again or call us.",
    sending: "Sending…",
  },
  emergency: {
    alertBefore: "Active water entry? Call first at",
    alertAfter: "Form response depends on crew availability and safe access.",
    name: "Name",
    phone: "Phone",
    email: "Email",
    location: "Address or postal code",
    city: "City",
    waterEntry: "Is water entering the building?",
    roofType: "Roof type, if known",
    photos: "Photos (optional)",
    description: "What happened?",
    submit: "Send urgent request",
    yesNo: options(["yes", "no", "unsure"], ["Yes", "No", "Not sure"]),
    roofTypes: options(
      ["Asphalt shingles", "Cedar", "Metal", "Flat / low-slope", "Other", "Unknown"],
      ["Asphalt shingles", "Cedar", "Metal", "Flat / low-slope", "Other", "Unknown"],
    ),
    errors: {
      name: "Name is required",
      phone: "Enter a valid phone number",
      email: "Enter a valid email",
      location: "Address or postal code is required",
      city: "City is required",
      description: "Describe the urgent problem",
      option: "Choose an option",
    },
  },
  planned: {
    intro:
      "This intake gathers information for review. It does not create an instant price or a guaranteed quote.",
    propertyLegend: "1. Property and location",
    roofLegend: "2. Service and roof details",
    contactLegend: "3. Contact",
    propertyType: "Property type",
    city: "City",
    address: "Address",
    postalCode: "Postal code",
    service: "Service",
    approximateSize: "Approximate roof size (optional)",
    sizePlaceholder: "e.g. 2,000 sq. ft. or unknown",
    stories: "Number of stories",
    slope: "Roof slope (optional)",
    skylights: "Skylights (optional)",
    complexity: "Roof complexity (optional)",
    existingRoof: "Existing roof type",
    roofAge: "Approximate roof age (optional)",
    agePlaceholder: "e.g. 18 years or unknown",
    desiredSystem: "Desired system (optional)",
    knownIssues: "Known issues (optional)",
    timeline: "Timing",
    photos: "Photos (optional)",
    name: "Name",
    phone: "Phone",
    email: "Email",
    preferredContact: "Preferred contact method",
    submit: "Submit project for review",
    propertyTypes: options(values.property, values.property),
    timelines: options(values.timeline, values.timeline),
    contactMethods: options(values.contact, values.contact),
    roofTypes: options(values.roof, values.roof),
    storiesOptions: options(values.stories, values.stories),
    slopeOptions: options(values.slope, values.slope),
    skylightOptions: options(values.skylights, values.skylights),
    complexityOptions: options(values.complexity, values.complexity),
    errors: {
      propertyType: "Choose a property type",
      address: "Address is required",
      city: "City is required",
      postalCode: "Postal code is required",
      service: "Choose a service",
      option: "Choose an option",
      timeline: "Choose a timeline",
      name: "Name is required",
      phone: "Enter a valid phone number",
      email: "Enter a valid email",
      contact: "Choose a contact method",
    },
  },
};

const fa: IntakeCopy = {
  common: {
    photoEmpty:
      "سامانه فعلی ثبت درخواست فقط داده‌های متنی را می‌پذیرد و امکان ارسال عکس ندارد. لطفاً پس از تماس تیم ما، عکس‌ها را جداگانه بفرستید.",
    photoSelected:
      "{count} عکس در مرورگر شما انتخاب شد، اما هیچ فایلی ارسال نشده است. لطفاً پس از تماس تیم ما، عکس‌ها را جداگانه بفرستید.",
    unavailable: "در حال حاضر امکان ارسال آنلاین فراهم نیست. لطفاً با ما تماس بگیرید.",
    sendError: "ارسال درخواست انجام نشد. لطفاً دوباره تلاش کنید یا با ما تماس بگیرید.",
    sending: "در حال ارسال…",
  },
  emergency: {
    alertBefore: "آیا آب وارد ساختمان می‌شود؟ ابتدا با این شماره تماس بگیرید:",
    alertAfter: "پاسخ‌گویی از طریق فرم به ظرفیت تیم و امکان دسترسی ایمن بستگی دارد.",
    name: "نام و نام خانوادگی",
    phone: "شماره تماس",
    email: "ایمیل",
    location: "نشانی یا کد پستی",
    city: "شهر",
    waterEntry: "آیا آب وارد ساختمان می‌شود؟",
    roofType: "نوع سقف، در صورت اطلاع",
    photos: "عکس‌ها (اختیاری)",
    description: "چه اتفاقی افتاده است؟",
    submit: "ارسال درخواست فوری",
    yesNo: options(["yes", "no", "unsure"], ["بله", "خیر", "مطمئن نیستم"]),
    roofTypes: options(
      ["Asphalt shingles", "Cedar", "Metal", "Flat / low-slope", "Other", "Unknown"],
      ["شینگل آسفالتی", "چوب سدر", "فلزی", "تخت یا کم‌شیب", "سایر", "نمی‌دانم"],
    ),
    errors: {
      name: "واردکردن نام الزامی است",
      phone: "شماره تماس معتبر وارد کنید",
      email: "ایمیل معتبر وارد کنید",
      location: "نشانی یا کد پستی الزامی است",
      city: "واردکردن شهر الزامی است",
      description: "مشکل فوری را توضیح دهید",
      option: "یک گزینه را انتخاب کنید",
    },
  },
  planned: {
    intro:
      "این فرم اطلاعات پروژه را برای بررسی جمع‌آوری می‌کند و قیمت فوری یا پیشنهاد قیمت قطعی ارائه نمی‌دهد.",
    propertyLegend: "۱. مشخصات و موقعیت ملک",
    roofLegend: "۲. خدمت موردنیاز و مشخصات سقف",
    contactLegend: "۳. اطلاعات تماس",
    propertyType: "نوع ملک",
    city: "شهر",
    address: "نشانی",
    postalCode: "کد پستی",
    service: "خدمت موردنیاز",
    approximateSize: "مساحت تقریبی سقف (اختیاری)",
    sizePlaceholder: "برای نمونه: ۲۰۰۰ فوت مربع یا نامشخص",
    stories: "تعداد طبقات",
    slope: "شیب سقف (اختیاری)",
    skylights: "نورگیر سقفی (اختیاری)",
    complexity: "پیچیدگی سقف (اختیاری)",
    existingRoof: "نوع سقف فعلی",
    roofAge: "عمر تقریبی سقف (اختیاری)",
    agePlaceholder: "برای نمونه: ۱۸ سال یا نامشخص",
    desiredSystem: "سیستم سقف موردنظر (اختیاری)",
    knownIssues: "مشکلات شناخته‌شده (اختیاری)",
    timeline: "زمان موردنظر",
    photos: "عکس‌ها (اختیاری)",
    name: "نام و نام خانوادگی",
    phone: "شماره تماس",
    email: "ایمیل",
    preferredContact: "روش ترجیحی تماس",
    submit: "ارسال پروژه برای بررسی",
    propertyTypes: options(values.property, [
      "مسکونی",
      "تجاری",
      "استراتا",
      "چندواحدی",
      "ساختمان نوساز",
    ]),
    timelines: options(values.timeline, [
      "فوری",
      "تا ۳۰ روز آینده",
      "۱ تا ۳ ماه",
      "۳ تا ۶ ماه",
      "برنامه‌ریزی یا برآورد بودجه",
    ]),
    contactMethods: options(values.contact, ["تلفن", "ایمیل", "هرکدام"]),
    roofTypes: options(values.roof, [
      "شینگل آسفالتی",
      "چوب سدر",
      "فلزی",
      "غشایی تخت یا کم‌شیب",
      "سفالی",
      "سایر",
      "نمی‌دانم",
    ]),
    storiesOptions: options(values.stories, ["۱", "۲", "۳", "۴ طبقه یا بیشتر", "نمی‌دانم"]),
    slopeOptions: options(values.slope, ["کم", "متوسط", "تند", "نمی‌دانم"]),
    skylightOptions: options(values.skylights, ["ندارد", "یکی", "چند مورد", "نمی‌دانم"]),
    complexityOptions: options(values.complexity, ["ساده", "معمولی", "پیچیده", "نمی‌دانم"]),
    errors: {
      propertyType: "نوع ملک را انتخاب کنید",
      address: "واردکردن نشانی الزامی است",
      city: "واردکردن شهر الزامی است",
      postalCode: "کد پستی الزامی است",
      service: "یک خدمت را انتخاب کنید",
      option: "یک گزینه را انتخاب کنید",
      timeline: "زمان موردنظر را انتخاب کنید",
      name: "واردکردن نام الزامی است",
      phone: "شماره تماس معتبر وارد کنید",
      email: "ایمیل معتبر وارد کنید",
      contact: "روش تماس را انتخاب کنید",
    },
  },
};

const ar: IntakeCopy = {
  common: {
    photoEmpty:
      "يقبل نظام إرسال الطلبات الحالي البيانات النصية فقط ولا ينقل الصور. يرجى إرسال الصور عند تواصل فريقنا معك.",
    photoSelected:
      "تم اختيار {count} صورة في متصفحك، لكن لم يُرسل أي ملف. يرجى إرسال الصور عند تواصل فريقنا معك.",
    unavailable: "الإرسال عبر الإنترنت غير متاح حالياً. يرجى الاتصال بنا.",
    sendError: "تعذّر إرسال الطلب. يرجى المحاولة مرة أخرى أو الاتصال بنا.",
    sending: "جارٍ الإرسال…",
  },
  emergency: {
    alertBefore: "هل تدخل المياه إلى المبنى؟ اتصل أولاً على الرقم:",
    alertAfter: "تعتمد الاستجابة للنموذج على توافر الطاقم وإمكانية الوصول الآمن.",
    name: "الاسم",
    phone: "رقم الهاتف",
    email: "البريد الإلكتروني",
    location: "العنوان أو الرمز البريدي",
    city: "المدينة",
    waterEntry: "هل تدخل المياه إلى المبنى؟",
    roofType: "نوع السقف إن كان معروفاً",
    photos: "الصور (اختياري)",
    description: "ماذا حدث؟",
    submit: "إرسال طلب عاجل",
    yesNo: options(["yes", "no", "unsure"], ["نعم", "لا", "غير متأكد"]),
    roofTypes: options(
      ["Asphalt shingles", "Cedar", "Metal", "Flat / low-slope", "Other", "Unknown"],
      ["قوباء إسفلتية", "خشب الأرز", "معدني", "مسطح أو منخفض الميل", "غير ذلك", "غير معروف"],
    ),
    errors: {
      name: "الاسم مطلوب",
      phone: "أدخل رقم هاتف صحيحاً",
      email: "أدخل بريداً إلكترونياً صحيحاً",
      location: "العنوان أو الرمز البريدي مطلوب",
      city: "المدينة مطلوبة",
      description: "صف المشكلة العاجلة",
      option: "اختر أحد الخيارات",
    },
  },
  planned: {
    intro: "يجمع هذا النموذج معلومات المشروع للمراجعة، ولا ينشئ سعراً فورياً أو عرضاً مضموناً.",
    propertyLegend: "١. العقار والموقع",
    roofLegend: "٢. الخدمة وتفاصيل السقف",
    contactLegend: "٣. معلومات الاتصال",
    propertyType: "نوع العقار",
    city: "المدينة",
    address: "العنوان",
    postalCode: "الرمز البريدي",
    service: "الخدمة المطلوبة",
    approximateSize: "مساحة السقف التقريبية (اختياري)",
    sizePlaceholder: "مثال: ٢٠٠٠ قدم مربعة أو غير معروف",
    stories: "عدد الطوابق",
    slope: "ميل السقف (اختياري)",
    skylights: "المناور (اختياري)",
    complexity: "تعقيد السقف (اختياري)",
    existingRoof: "نوع السقف الحالي",
    roofAge: "العمر التقريبي للسقف (اختياري)",
    agePlaceholder: "مثال: ١٨ سنة أو غير معروف",
    desiredSystem: "نظام السقف المطلوب (اختياري)",
    knownIssues: "المشكلات المعروفة (اختياري)",
    timeline: "التوقيت",
    photos: "الصور (اختياري)",
    name: "الاسم",
    phone: "رقم الهاتف",
    email: "البريد الإلكتروني",
    preferredContact: "وسيلة الاتصال المفضلة",
    submit: "إرسال المشروع للمراجعة",
    propertyTypes: options(values.property, [
      "سكني",
      "تجاري",
      "ستراتا",
      "متعدد الوحدات",
      "إنشاء جديد",
    ]),
    timelines: options(values.timeline, [
      "عاجل",
      "خلال ٣٠ يوماً",
      "من شهر إلى ٣ أشهر",
      "من ٣ إلى ٦ أشهر",
      "للتخطيط أو إعداد الميزانية",
    ]),
    contactMethods: options(values.contact, ["الهاتف", "البريد الإلكتروني", "أي منهما"]),
    roofTypes: options(values.roof, [
      "قوباء إسفلتية",
      "خشب الأرز",
      "معدني",
      "غشاء مسطح أو منخفض الميل",
      "قرميد",
      "غير ذلك",
      "غير معروف",
    ]),
    storiesOptions: options(values.stories, ["١", "٢", "٣", "٤ أو أكثر", "غير معروف"]),
    slopeOptions: options(values.slope, ["منخفض", "متوسط", "حاد", "غير معروف"]),
    skylightOptions: options(values.skylights, ["لا يوجد", "واحد", "عدة مناور", "غير معروف"]),
    complexityOptions: options(values.complexity, ["بسيط", "معتاد", "معقد", "غير معروف"]),
    errors: {
      propertyType: "اختر نوع العقار",
      address: "العنوان مطلوب",
      city: "المدينة مطلوبة",
      postalCode: "الرمز البريدي مطلوب",
      service: "اختر خدمة",
      option: "اختر أحد الخيارات",
      timeline: "اختر التوقيت",
      name: "الاسم مطلوب",
      phone: "أدخل رقم هاتف صحيحاً",
      email: "أدخل بريداً إلكترونياً صحيحاً",
      contact: "اختر وسيلة الاتصال",
    },
  },
};

// Persian and Arabic are fully native; the remaining translated routes use
// the safe English fallback until their marketing review is complete.
const copyByLocale: Record<LocaleCode, IntakeCopy> = {
  "en-ca": en,
  "zh-cn": en,
  "zh-tw": en,
  "pa-in": en,
  "ko-kr": en,
  "fa-ir": fa,
  "ar-sa": ar,
};

export function intakeCopy(locale: LocaleCode): IntakeCopy {
  return copyByLocale[locale];
}
